import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Shield } from 'lucide-react';
type Tab = 'privacy' | 'terms';
export function LegalPage() {
  const [activeTab, setActiveTab] = useState<Tab>('privacy');
  const navigate = useNavigate();
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <div className="min-h-screen bg-cream pt-20" data-id="element-188">
      {/* Header */}
      <div className="bg-white border-b border-charcoal/10" data-id="element-189">
        <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-190">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm text-charcoal-light hover:text-gold transition-colors mb-6" data-id="element-191">
            <ArrowLeft className="w-4 h-4" data-id="element-192" />
            Back to Home
          </button>

          <h1 className="text-4xl md:text-5xl font-serif mb-4" data-id="element-193">
            Legal Information
          </h1>
          <p className="text-charcoal-light font-light" data-id="element-194">
            Last Updated: June 13, 2026
          </p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="bg-white border-b border-charcoal/10 sticky top-20 z-40" data-id="element-195">
        <div className="max-w-7xl mx-auto px-6" data-id="element-196">
          <div className="flex gap-8" data-id="element-197">
            <button onClick={() => setActiveTab('privacy')} className={`flex items-center gap-2 py-4 border-b-2 transition-colors ${activeTab === 'privacy' ? 'border-gold text-charcoal' : 'border-transparent text-charcoal-light hover:text-charcoal'}`} data-id="element-198">
              <Shield className="w-4 h-4" data-id="element-199" />
              <span className="font-medium tracking-wide" data-id="element-200">Privacy Policy</span>
            </button>
            <button onClick={() => setActiveTab('terms')} className={`flex items-center gap-2 py-4 border-b-2 transition-colors ${activeTab === 'terms' ? 'border-gold text-charcoal' : 'border-transparent text-charcoal-light hover:text-charcoal'}`} data-id="element-201">
              <FileText className="w-4 h-4" data-id="element-202" />
              <span className="font-medium tracking-wide" data-id="element-203">
                Terms & Conditions
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12" data-id="element-204">
        <div className="grid lg:grid-cols-4 gap-12" data-id="element-205">
          {/* Sidebar Navigation */}
          <aside className="lg:col-span-1" data-id="element-206">
            <div className="sticky top-40 bg-white p-6 border border-charcoal/10" data-id="element-207">
              <h3 className="text-sm font-semibold tracking-[0.2em] uppercase text-gold mb-4" data-id="element-208">
                Quick Navigation
              </h3>
              {activeTab === 'privacy' ? <nav className="space-y-3" data-id="element-209">
                  <button onClick={() => scrollToSection('intro')} className="block text-sm text-charcoal-light hover:text-gold transition-colors text-left" data-id="element-210">
                    Introduction
                  </button>
                  <button onClick={() => scrollToSection('data-collected')} className="block text-sm text-charcoal-light hover:text-gold transition-colors text-left" data-id="element-211">
                    1. Information We Collect
                  </button>
                  <button onClick={() => scrollToSection('purpose')} className="block text-sm text-charcoal-light hover:text-gold transition-colors text-left" data-id="element-212">
                    2. Purpose of Processing
                  </button>
                  <button onClick={() => scrollToSection('uae-compliance')} className="block text-sm text-charcoal-light hover:text-gold transition-colors text-left" data-id="element-213">
                    3. UAE Legal Framework
                  </button>
                  <button onClick={() => scrollToSection('security')} className="block text-sm text-charcoal-light hover:text-gold transition-colors text-left" data-id="element-214">
                    4. Data Security & Retention
                  </button>
                  <button onClick={() => scrollToSection('rights')} className="block text-sm text-charcoal-light hover:text-gold transition-colors text-left" data-id="element-215">
                    5. Your Rights
                  </button>
                  <button onClick={() => scrollToSection('contact-privacy')} className="block text-sm text-charcoal-light hover:text-gold transition-colors text-left" data-id="element-216">
                    6. Contact Us
                  </button>
                </nav> : <nav className="space-y-3" data-id="element-217">
                  <button onClick={() => scrollToSection('acceptance')} className="block text-sm text-charcoal-light hover:text-gold transition-colors text-left" data-id="element-218">
                    Acceptance of Terms
                  </button>
                  <button onClick={() => scrollToSection('advisory-nature')} className="block text-sm text-charcoal-light hover:text-gold transition-colors text-left" data-id="element-219">
                    1. Advisory Nature of Services
                  </button>
                  <button onClick={() => scrollToSection('governing-law')} className="block text-sm text-charcoal-light hover:text-gold transition-colors text-left" data-id="element-220">
                    2. Governing Law
                  </button>
                  <button onClick={() => scrollToSection('user-obligations')} className="block text-sm text-charcoal-light hover:text-gold transition-colors text-left" data-id="element-221">
                    3. User Obligations
                  </button>
                  <button onClick={() => scrollToSection('intellectual-property')} className="block text-sm text-charcoal-light hover:text-gold transition-colors text-left" data-id="element-222">
                    4. Intellectual Property
                  </button>
                  <button onClick={() => scrollToSection('limitation')} className="block text-sm text-charcoal-light hover:text-gold transition-colors text-left" data-id="element-223">
                    5. Limitation of Liability
                  </button>
                  <button onClick={() => scrollToSection('contact-terms')} className="block text-sm text-charcoal-light hover:text-gold transition-colors text-left" data-id="element-224">
                    6. Contact Information
                  </button>
                </nav>}
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3" data-id="element-225">
            {activeTab === 'privacy' ? <PrivacyContent data-id="element-226" /> : <TermsContent data-id="element-227" />}
          </main>
        </div>
      </div>
    </div>;
}
function PrivacyContent() {
  return <div className="prose prose-lg max-w-none" data-id="element-228">
      <section id="intro" className="bg-white p-8 mb-8 border border-charcoal/10" data-id="element-229">
        <h2 className="text-3xl font-serif mb-4" data-id="element-230">Privacy Policy</h2>
        <p className="text-charcoal-light font-light leading-relaxed" data-id="element-231">
          DUBAI BEAUTY Advisory ("we," "our," or "us") is committed to
          protecting the privacy and security of your personal information. This
          Privacy Policy explains how we collect, use, disclose, and safeguard
          your information when you visit our website and use our business
          consultancy services for beauty businesses in Dubai, UAE.
        </p>
      </section>

      <section id="data-collected" className="bg-white p-8 mb-8 border border-charcoal/10" data-id="element-232">
        <h3 className="text-2xl font-serif mb-4" data-id="element-233">1. Information We Collect</h3>
        <p className="text-charcoal-light font-light leading-relaxed mb-4" data-id="element-234">
          We collect information that you provide directly to us when requesting
          a consultation or engaging with our services:
        </p>
        <div className="bg-cream p-6 mb-4" data-id="element-235">
          <h4 className="font-semibold mb-3" data-id="element-236">Personal Contact Information:</h4>
          <ul className="space-y-2 text-charcoal-light font-light" data-id="element-237">
            <li data-id="element-238">• Full Name</li>
            <li data-id="element-239">• Phone Number / WhatsApp Contact</li>
            <li data-id="element-240">• Email Address</li>
          </ul>
        </div>
        <div className="bg-cream p-6" data-id="element-241">
          <h4 className="font-semibold mb-3" data-id="element-242">
            Business & Investment Information:
          </h4>
          <ul className="space-y-2 text-charcoal-light font-light" data-id="element-243">
            <li data-id="element-244">
              • Type of beauty business you are interested in (salon, aesthetic
              clinic, spa, wellness center, new concept)
            </li>
            <li data-id="element-245">• Investment budget range (in AED)</li>
            <li data-id="element-246">
              • Additional details or messages you provide about your goals and
              requirements
            </li>
          </ul>
        </div>
      </section>

      <section id="purpose" className="bg-white p-8 mb-8 border border-charcoal/10" data-id="element-247">
        <h3 className="text-2xl font-serif mb-4" data-id="element-248">2. Purpose of Processing</h3>
        <p className="text-charcoal-light font-light leading-relaxed mb-4" data-id="element-249">
          We process your personal data for the following purposes:
        </p>
        <ul className="space-y-3 text-charcoal-light font-light" data-id="element-250">
          <li className="flex items-start gap-3" data-id="element-251">
            <span className="text-gold mt-1" data-id="element-252">•</span>
            <span data-id="element-253">
              To provide business consultation and advisory services related to
              buying, evaluating, or launching beauty businesses in Dubai
            </span>
          </li>
          <li className="flex items-start gap-3" data-id="element-254">
            <span className="text-gold mt-1" data-id="element-255">•</span>
            <span data-id="element-256">
              To develop customized business plans, financial models, and
              operational roadmaps
            </span>
          </li>
          <li className="flex items-start gap-3" data-id="element-257">
            <span className="text-gold mt-1" data-id="element-258">•</span>
            <span data-id="element-259">
              To contact you via WhatsApp, email, or phone to discuss your
              consultation request and business goals
            </span>
          </li>
          <li className="flex items-start gap-3" data-id="element-260">
            <span className="text-gold mt-1" data-id="element-261">•</span>
            <span data-id="element-262">
              To conduct business evaluations and due diligence on beauty
              businesses for sale
            </span>
          </li>
          <li className="flex items-start gap-3" data-id="element-263">
            <span className="text-gold mt-1" data-id="element-264">•</span>
            <span data-id="element-265">
              To improve our services and understand client needs in the UAE
              beauty market
            </span>
          </li>
        </ul>
      </section>

      <section id="uae-compliance" className="bg-white p-8 mb-8 border border-charcoal/10" data-id="element-266">
        <h3 className="text-2xl font-serif mb-4" data-id="element-267">
          3. UAE Legal Framework & Compliance
        </h3>
        <div className="bg-gold/5 border-l-4 border-gold p-6 mb-4" data-id="element-268">
          <p className="font-medium mb-2" data-id="element-269">
            UAE Personal Data Protection Law (PDPL)
          </p>
          <p className="text-charcoal-light font-light leading-relaxed" data-id="element-270">
            We comply with UAE Federal Decree-Law No. 45 of 2021 on the
            Protection of Personal Data and its implementing regulations. This
            law governs the processing of personal data in the United Arab
            Emirates and ensures your rights are protected.
          </p>
        </div>
        <p className="text-charcoal-light font-light leading-relaxed mb-4" data-id="element-271">
          As a business consultancy operating in Dubai, we adhere to the highest
          standards of data protection and privacy. We process your data
          lawfully, fairly, and transparently, and only for the specific
          purposes outlined in this policy.
        </p>
        <p className="text-charcoal-light font-light leading-relaxed" data-id="element-272">
          Your personal data is processed based on your consent when you submit
          a consultation request, and for the legitimate interests of providing
          professional business advisory services.
        </p>
      </section>

      <section id="security" className="bg-white p-8 mb-8 border border-charcoal/10" data-id="element-273">
        <h3 className="text-2xl font-serif mb-4" data-id="element-274">
          4. Data Security & Retention
        </h3>
        <h4 className="font-semibold mb-3" data-id="element-275">Security Measures</h4>
        <p className="text-charcoal-light font-light leading-relaxed mb-4" data-id="element-276">
          We implement appropriate technical and organizational security
          measures to protect your personal data against unauthorized access,
          alteration, disclosure, or destruction. Given the sensitive nature of
          investment and financial information, we employ industry-standard
          encryption and secure storage practices.
        </p>
        <h4 className="font-semibold mb-3" data-id="element-277">Data Retention</h4>
        <p className="text-charcoal-light font-light leading-relaxed mb-4" data-id="element-278">
          We retain your personal data only for as long as necessary to fulfill
          the purposes outlined in this Privacy Policy, or as required by UAE
          law. Consultation requests and business evaluation data are typically
          retained for:
        </p>
        <ul className="space-y-2 text-charcoal-light font-light mb-4" data-id="element-279">
          <li data-id="element-280">
            • Active client engagements: Duration of the project plus 2 years
          </li>
          <li data-id="element-281">• Consultation inquiries (not converted): Up to 1 year</li>
          <li data-id="element-282">
            • Financial and business analysis records: As required by UAE
            commercial law (minimum 5 years)
          </li>
        </ul>
        <p className="text-charcoal-light font-light leading-relaxed" data-id="element-283">
          After the retention period, your data will be securely deleted or
          anonymized.
        </p>
      </section>

      <section id="rights" className="bg-white p-8 mb-8 border border-charcoal/10" data-id="element-284">
        <h3 className="text-2xl font-serif mb-4" data-id="element-285">
          5. Your Rights Under UAE PDPL
        </h3>
        <p className="text-charcoal-light font-light leading-relaxed mb-4" data-id="element-286">
          Under the UAE Personal Data Protection Law, you have the following
          rights:
        </p>
        <div className="grid md:grid-cols-2 gap-4" data-id="element-287">
          <div className="bg-cream p-4" data-id="element-288">
            <h5 className="font-semibold mb-2" data-id="element-289">Right to Access</h5>
            <p className="text-sm text-charcoal-light font-light" data-id="element-290">
              Request a copy of the personal data we hold about you
            </p>
          </div>
          <div className="bg-cream p-4" data-id="element-291">
            <h5 className="font-semibold mb-2" data-id="element-292">Right to Rectification</h5>
            <p className="text-sm text-charcoal-light font-light" data-id="element-293">
              Request correction of inaccurate or incomplete data
            </p>
          </div>
          <div className="bg-cream p-4" data-id="element-294">
            <h5 className="font-semibold mb-2" data-id="element-295">Right to Erasure</h5>
            <p className="text-sm text-charcoal-light font-light" data-id="element-296">
              Request deletion of your personal data under certain conditions
            </p>
          </div>
          <div className="bg-cream p-4" data-id="element-297">
            <h5 className="font-semibold mb-2" data-id="element-298">Right to Object</h5>
            <p className="text-sm text-charcoal-light font-light" data-id="element-299">
              Object to processing of your data for specific purposes
            </p>
          </div>
        </div>
        <p className="text-charcoal-light font-light leading-relaxed mt-4" data-id="element-300">
          To exercise any of these rights, please contact us using the
          information provided below.
        </p>
      </section>

      <section id="contact-privacy" className="bg-charcoal text-cream p-8 border border-charcoal/10" data-id="element-301">
        <h3 className="text-2xl font-serif mb-4" data-id="element-302">6. Contact Us</h3>
        <p className="text-cream-dark font-light leading-relaxed mb-4" data-id="element-303">
          If you have any questions about this Privacy Policy or wish to
          exercise your data protection rights, please contact us:
        </p>
        <div className="space-y-2 text-cream-dark font-light" data-id="element-304">
          <p data-id="element-305">
            <strong className="text-cream" data-id="element-306">Email:</strong>{' '}
            privacy@dubaibeautyadvisory.com
          </p>
          <p data-id="element-307">
            <strong className="text-cream" data-id="element-308">WhatsApp:</strong> +971 50 155 6516
          </p>
          <p data-id="element-309">
            <strong className="text-cream" data-id="element-310">Address:</strong> Dubai, United Arab
            Emirates
          </p>
        </div>
      </section>
    </div>;
}
function TermsContent() {
  return <div className="prose prose-lg max-w-none" data-id="element-311">
      <section id="acceptance" className="bg-white p-8 mb-8 border border-charcoal/10" data-id="element-312">
        <h2 className="text-3xl font-serif mb-4" data-id="element-313">Terms & Conditions</h2>
        <p className="text-charcoal-light font-light leading-relaxed" data-id="element-314">
          Welcome to DUBAI BEAUTY Advisory. By accessing our website and using
          our services, you agree to be bound by these Terms and Conditions.
          Please read them carefully before engaging with our consultancy
          services.
        </p>
      </section>

      <section id="advisory-nature" className="bg-white p-8 mb-8 border border-charcoal/10" data-id="element-315">
        <h3 className="text-2xl font-serif mb-4" data-id="element-316">
          1. Advisory Nature of Services
        </h3>
        <div className="bg-gold/5 border-l-4 border-gold p-6 mb-4" data-id="element-317">
          <p className="font-medium mb-2" data-id="element-318">Important Disclaimer</p>
          <p className="text-charcoal-light font-light leading-relaxed" data-id="element-319">
            DUBAI BEAUTY Advisory provides business consultation and evaluation
            services. We are NOT a licensed legal firm, tax advisory, or
            financial institution. Our services do not constitute formal legal,
            tax, or guaranteed investment advice.
          </p>
        </div>
        <h4 className="font-semibold mb-3" data-id="element-320">Nature of Our Services</h4>
        <ul className="space-y-3 text-charcoal-light font-light mb-4" data-id="element-321">
          <li className="flex items-start gap-3" data-id="element-322">
            <span className="text-gold mt-1" data-id="element-323">•</span>
            <span data-id="element-324">
              We provide strategic business consultation, market analysis, and
              operational roadmaps for beauty businesses in Dubai
            </span>
          </li>
          <li className="flex items-start gap-3" data-id="element-325">
            <span className="text-gold mt-1" data-id="element-326">•</span>
            <span data-id="element-327">
              Business evaluations, financial models, and ROI projections are
              estimates based on market research and industry expertise
            </span>
          </li>
          <li className="flex items-start gap-3" data-id="element-328">
            <span className="text-gold mt-1" data-id="element-329">•</span>
            <span data-id="element-330">
              Break-even timelines and revenue projections are forecasts, not
              guarantees of actual business performance
            </span>
          </li>
          <li className="flex items-start gap-3" data-id="element-331">
            <span className="text-gold mt-1" data-id="element-332">•</span>
            <span data-id="element-333">
              Clients are responsible for conducting their own due diligence and
              seeking independent legal and financial advice before making
              investment decisions
            </span>
          </li>
        </ul>
        <p className="text-charcoal-light font-light leading-relaxed" data-id="element-334">
          While we strive to provide accurate and valuable insights, actual
          business outcomes depend on numerous factors including market
          conditions, execution quality, regulatory changes, and economic
          circumstances beyond our control.
        </p>
      </section>

      <section id="governing-law" className="bg-white p-8 mb-8 border border-charcoal/10" data-id="element-335">
        <h3 className="text-2xl font-serif mb-4" data-id="element-336">
          2. Governing Law & Jurisdiction
        </h3>
        <p className="text-charcoal-light font-light leading-relaxed mb-4" data-id="element-337">
          These Terms and Conditions are governed by and construed in accordance
          with the laws of the United Arab Emirates.
        </p>
        <div className="bg-cream p-6 mb-4" data-id="element-338">
          <h4 className="font-semibold mb-3" data-id="element-339">Jurisdiction</h4>
          <p className="text-charcoal-light font-light leading-relaxed" data-id="element-340">
            Any disputes arising from or related to these Terms or our services
            shall be subject to the exclusive jurisdiction of the courts of
            Dubai, United Arab Emirates.
          </p>
        </div>
        <div className="bg-cream p-6" data-id="element-341">
          <h4 className="font-semibold mb-3" data-id="element-342">Regulatory Compliance</h4>
          <p className="text-charcoal-light font-light leading-relaxed mb-2" data-id="element-343">
            Our services are provided in compliance with:
          </p>
          <ul className="space-y-2 text-charcoal-light font-light" data-id="element-344">
            <li data-id="element-345">• Department of Economy and Tourism (DET) regulations</li>
            <li data-id="element-346">
              • Dubai Health Authority (DHA) guidelines for health and beauty
              businesses
            </li>
            <li data-id="element-347">• UAE Commercial Companies Law</li>
            <li data-id="element-348">
              • UAE Federal Decree-Law No. 45 of 2021 on Personal Data
              Protection
            </li>
          </ul>
        </div>
      </section>

      <section id="user-obligations" className="bg-white p-8 mb-8 border border-charcoal/10" data-id="element-349">
        <h3 className="text-2xl font-serif mb-4" data-id="element-350">3. User Obligations</h3>
        <p className="text-charcoal-light font-light leading-relaxed mb-4" data-id="element-351">
          When using our services and submitting consultation requests, you
          agree to:
        </p>
        <div className="space-y-4" data-id="element-352">
          <div className="border-l-2 border-gold pl-4" data-id="element-353">
            <h4 className="font-semibold mb-2" data-id="element-354">Accuracy of Information</h4>
            <p className="text-charcoal-light font-light leading-relaxed" data-id="element-355">
              Provide accurate, complete, and truthful information regarding
              your investment budget, business goals, financial capacity, and
              contact details. Inaccurate information may result in flawed
              business evaluations and recommendations.
            </p>
          </div>
          <div className="border-l-2 border-gold pl-4" data-id="element-356">
            <h4 className="font-semibold mb-2" data-id="element-357">Confidentiality</h4>
            <p className="text-charcoal-light font-light leading-relaxed" data-id="element-358">
              Maintain confidentiality of any proprietary business analysis,
              financial models, or strategic recommendations provided by DUBAI
              BEAUTY Advisory. You may not share, reproduce, or distribute our
              deliverables without prior written consent.
            </p>
          </div>
          <div className="border-l-2 border-gold pl-4" data-id="element-359">
            <h4 className="font-semibold mb-2" data-id="element-360">Independent Verification</h4>
            <p className="text-charcoal-light font-light leading-relaxed" data-id="element-361">
              Conduct your own independent verification of any business
              opportunity, financial projection, or market analysis before
              making investment decisions. Consult with licensed legal and
              financial professionals as needed.
            </p>
          </div>
        </div>
      </section>

      <section id="intellectual-property" className="bg-white p-8 mb-8 border border-charcoal/10" data-id="element-362">
        <h3 className="text-2xl font-serif mb-4" data-id="element-363">
          4. Intellectual Property Rights
        </h3>
        <p className="text-charcoal-light font-light leading-relaxed mb-4" data-id="element-364">
          All content, materials, and deliverables provided by DUBAI BEAUTY
          Advisory are protected by intellectual property laws.
        </p>
        <h4 className="font-semibold mb-3" data-id="element-365">Protected Materials Include:</h4>
        <ul className="space-y-2 text-charcoal-light font-light mb-4" data-id="element-366">
          <li data-id="element-367">• Proprietary consulting frameworks and methodologies</li>
          <li data-id="element-368">• Business model analysis templates</li>
          <li data-id="element-369">• Financial evaluation models and calculators</li>
          <li data-id="element-370">• Operational roadmaps and launch checklists</li>
          <li data-id="element-371">• Market research reports and industry insights</li>
          <li data-id="element-372">• Brand identity, logos, and website content</li>
        </ul>
        <div className="bg-gold/5 p-6" data-id="element-373">
          <p className="text-charcoal-light font-light leading-relaxed" data-id="element-374">
            <strong data-id="element-375">License Grant:</strong> Upon payment for services, you
            receive a limited, non-exclusive, non-transferable license to use
            the deliverables solely for your internal business purposes related
            to the specific project. You may not resell, redistribute, or
            commercialize our frameworks or analysis.
          </p>
        </div>
      </section>

      <section id="limitation" className="bg-white p-8 mb-8 border border-charcoal/10" data-id="element-376">
        <h3 className="text-2xl font-serif mb-4" data-id="element-377">5. Limitation of Liability</h3>
        <p className="text-charcoal-light font-light leading-relaxed mb-4" data-id="element-378">
          To the fullest extent permitted by UAE law:
        </p>
        <ul className="space-y-3 text-charcoal-light font-light" data-id="element-379">
          <li className="flex items-start gap-3" data-id="element-380">
            <span className="text-gold mt-1" data-id="element-381">•</span>
            <span data-id="element-382">
              DUBAI BEAUTY Advisory shall not be liable for any indirect,
              incidental, consequential, or punitive damages arising from the
              use of our services
            </span>
          </li>
          <li className="flex items-start gap-3" data-id="element-383">
            <span className="text-gold mt-1" data-id="element-384">•</span>
            <span data-id="element-385">
              We are not responsible for business losses, investment losses, or
              opportunity costs resulting from decisions made based on our
              consultation
            </span>
          </li>
          <li className="flex items-start gap-3" data-id="element-386">
            <span className="text-gold mt-1" data-id="element-387">•</span>
            <span data-id="element-388">
              Our liability is limited to the fees paid for the specific service
              in question
            </span>
          </li>
          <li className="flex items-start gap-3" data-id="element-389">
            <span className="text-gold mt-1" data-id="element-390">•</span>
            <span data-id="element-391">
              We do not guarantee specific business outcomes, revenue levels, or
              investment returns
            </span>
          </li>
        </ul>
      </section>

      <section id="contact-terms" className="bg-charcoal text-cream p-8 border border-charcoal/10" data-id="element-392">
        <h3 className="text-2xl font-serif mb-4" data-id="element-393">6. Contact Information</h3>
        <p className="text-cream-dark font-light leading-relaxed mb-4" data-id="element-394">
          For questions about these Terms and Conditions or our services:
        </p>
        <div className="space-y-2 text-cream-dark font-light" data-id="element-395">
          <p data-id="element-396">
            <strong className="text-cream" data-id="element-397">Email:</strong>{' '}
            legal@dubaibeautyadvisory.com
          </p>
          <p data-id="element-398">
            <strong className="text-cream" data-id="element-399">WhatsApp:</strong> +971 50 155 6516
          </p>
          <p data-id="element-400">
            <strong className="text-cream" data-id="element-401">Business Address:</strong> Dubai,
            United Arab Emirates
          </p>
        </div>
        <div className="mt-6 pt-6 border-t border-cream/20" data-id="element-402">
          <p className="text-sm text-cream-dark font-light" data-id="element-403">
            By submitting a consultation request or engaging with our services,
            you acknowledge that you have read, understood, and agree to be
            bound by these Terms and Conditions.
          </p>
        </div>
      </section>
    </div>;
}